import { useState } from "react";
import type { Dashboard, GroupCounts } from "../../api/dashboard";
import { Card, Segmented } from "../../components/ui";

type Grouping = "cluster" | "region";

const GROUPINGS = [
  { value: "cluster", label: "Cluster" },
  { value: "region", label: "Region" },
] as const;

/** The same status split, per cluster or per region. Bar length is the group's
 * size against the largest, so the eye reads both where the work is and what
 * state it is in. */
export function GroupCard({ data, colours }: { data: Dashboard; colours: Record<string, string> }) {
  const [by, setBy] = useState<Grouping>("cluster");
  const rows = by === "cluster" ? data.by_cluster : data.by_region;
  const order = data.status_key.map((k) => k.status);
  const labels = Object.fromEntries(data.status_key.map((k) => [k.status, k.label]));
  return (
    <Card
      className="dash-card"
      title={by === "cluster" ? "By cluster" : "By region"}
      sub={by === "cluster" && data.hidden_clusters > 0 ? `${data.hidden_clusters} smaller not shown` : undefined}
      actions={<Segmented options={GROUPINGS} value={by} onChange={setBy} label="Group sites by" />}
    >
      {rows.length === 0 ? (
        <p className="empty-state">No sites to group yet.</p>
      ) : (
        <GroupBars rows={rows} order={order} colours={colours} labels={labels} />
      )}
    </Card>
  );
}

function sumOf(counts: Record<string, number>): number {
  return Object.values(counts).reduce((a, b) => a + b, 0);
}

function GroupBars({
  rows,
  order,
  colours,
  labels,
}: {
  rows: GroupCounts[];
  order: string[];
  colours: Record<string, string>;
  labels: Record<string, string>;
}) {
  const max = Math.max(1, ...rows.map((row) => sumOf(row.counts)));
  return (
    <ul className="group-bars">
      {rows.map((row) => {
        const total = sumOf(row.counts);
        return (
          <li key={row.name}>
            <span className="group-name" title={row.name}>
              {row.name}
            </span>
            <span className="group-track">
              <span className="group-fill" style={{ width: `${(total / max) * 100}%` }}>
                {/* Slices in the legend's order, so each colour sits in the
                    same place on every bar. */}
                {order
                  .filter((status) => (row.counts[status] ?? 0) > 0)
                  .map((status) => (
                    <span
                      key={status}
                      title={`${labels[status] ?? status}: ${row.counts[status]}`}
                      style={{
                        width: `${(row.counts[status] / total) * 100}%`,
                        background: colours[status] ?? "var(--ink-3)",
                      }}
                    />
                  ))}
              </span>
            </span>
            <span className="group-total">{total}</span>
          </li>
        );
      })}
    </ul>
  );
}
